import React, { useContext, useEffect, useMemo, useState } from "react";

import {
  actualMatchMinute,
  formatDate,
  formatTimeWithSeparatePeriod,
  fullMatchLengthMinutesRaw,
  MATCH_STATUS,
  matchLengthMinutesRaw,
  matchStatusDate
} from "../../../../config/utils";
import { ClavaContext } from "../../../../config/contexts";

const MatchStatusDisplay: React.FC<{
  startDate: number;
  endTime: number;
  hideLive?: boolean;
  hideMinute?: boolean;
  fullDate?: boolean;
  sectionAmount: number;
  sectionDuration: number;
  pauseDuration: number;
  compact?: boolean;
  statusOverride?: MATCH_STATUS;
  mode?: "tile" | "matchpage";
}> = React.memo(
  ({
     startDate,
     sectionAmount,
     sectionDuration,
     pauseDuration,
     fullDate,
     hideLive,
     endTime,
     hideMinute,
     compact,
     statusOverride,
     mode = "tile"
   }) => {
    const { l } = useContext(ClavaContext);

    const matchLength = useMemo(
      () => matchLengthMinutesRaw(sectionAmount, sectionDuration),
      [sectionAmount, sectionDuration]
    );
    const [internalStatus, setInternalStatus] = useState<MATCH_STATUS>(() =>
      matchStatusDate(
        startDate,
        fullMatchLengthMinutesRaw(
          sectionAmount,
          sectionDuration,
          pauseDuration
        ),
        endTime
      )
    );
    useEffect(() => {
      if (statusOverride !== undefined) return undefined;
      const interval = setInterval(() => {
        setInternalStatus(
          matchStatusDate(
            startDate,
            fullMatchLengthMinutesRaw(
              sectionAmount,
              sectionDuration,
              pauseDuration
            ),
            endTime
          )
        );
      }, 10000);
      return () => {
        clearInterval(interval);
      };
    }, [endTime, pauseDuration, sectionAmount, sectionDuration, startDate, statusOverride]);

    useEffect(() => {
      if (statusOverride !== undefined) return undefined;
      setInternalStatus(
        matchStatusDate(
          startDate,
          fullMatchLengthMinutesRaw(
            sectionAmount,
            sectionDuration,
            pauseDuration
          ),
          endTime
        )
      );
      return undefined;
    }, [startDate, endTime, sectionAmount, sectionDuration, pauseDuration, statusOverride]);

    const status: MATCH_STATUS = statusOverride !== undefined ? statusOverride : internalStatus;
    if (typeof status === "number" || status === "ot")
      return (
        <div className="flex flex-col items-center justify-center text-center">
          {!hideLive && (
            <span
              className={
                compact
                  ? `font-medium text-sm text-center ${mode === "matchpage" ? "text-font dark:text-font-dark" : "text-font-600 dark:text-font-600-dark"}`
                  : `font-medium text-base text-center ${mode === "matchpage" ? "text-font dark:text-font-dark" : "text-font-600 dark:text-font-600-dark"}`
              }
            >
              LIVE
            </span>
          )}
          {!hideMinute && (
            <span
              className={
                compact
                  ? `font-medium text-sm text-center ${mode === "matchpage" ? "text-font dark:text-font-dark" : "text-font-600 dark:text-font-600-dark"}`
                  : `font-medium text-base text-center ${mode === "matchpage" ? "text-font dark:text-font-dark" : "text-font-600 dark:text-font-600-dark"}`
              }
            >
              {typeof status === "number"
                ? `${actualMatchMinute(
                  status,
                  sectionAmount,
                  sectionDuration,
                  pauseDuration
                ).toString(10)}'`
                : `${matchLength}+`}
            </span>
          )}
        </div>
      );
    if (status === "past")
      return (
        <span
          className={
            compact
              ? `text-xs text-center ${mode === "matchpage" ? "font-regular text-font-600 dark:text-font-600-dark" : "font-medium text-font-600 dark:text-font-600-dark"}`
              : `text-base text-center ${mode === "matchpage" ? "font-regular text-font-600 dark:text-font-600-dark" : "font-medium text-font-600 dark:text-font-600-dark"}`
          }
        >
          FT
        </span>
      );
    return (
      mode === "matchpage" ? (
        <div className="relative w-full flex items-center justify-center">
          <span
            className={
              compact
                ? "text-sm font-medium text-center text-font dark:text-font-dark"
                : "font-medium text-base text-center text-font dark:text-font-dark"
            }
          >
            {formatDate(new Date(startDate), l, false, true)}
          </span>
          {!!fullDate && (
            <div className="absolute inset-x-0 top-1/2 mt-3 flex items-center justify-center">
              <span
                className={
                  compact
                    ? "text-xs text-center font-regular text-font-600 dark:text-font-600-dark whitespace-nowrap"
                    : "text-sm text-center font-regular text-font-600 dark:text-font-600-dark whitespace-nowrap"
                }
              >
                {formatDate(new Date(startDate), l, false, false, true, true)}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center text-center">
          {(() => {
            const timeFormatted = formatTimeWithSeparatePeriod(new Date(startDate), l);
            return (
              <>
                <span
                  className={
                    compact
                      ? "text-xs font-medium text-center text-font-600 dark:text-font-600-dark"
                      : "font-medium text-base text-center text-font-600 dark:text-font-600-dark"
                  }
                >
                  {timeFormatted.time}
                </span>
                <span
                  className={
                    compact
                      ? "text-xs font-medium text-center text-font-600 dark:text-font-600-dark"
                      : "text-xs font-medium text-center text-font-600 dark:text-font-600-dark"
                  }
                >
                  {timeFormatted.period}
                </span>
              </>
            );
          })()}
          {!!fullDate && (
            <span
              className={
                compact
                  ? "text-xs text-center text-font-600 dark:text-font-600-dark"
                  : "text-sm text-center font-medium text-font-600 dark:text-font-600-dark"
              }
            >
              {formatDate(new Date(startDate), l, false, false, true, true)}
            </span>
          )}
        </div>
      )
    );
  },
  (prevProps, nextProps) => prevProps.startDate === nextProps.startDate
);

export default MatchStatusDisplay;
